<template>
  <div
    ref="containerRef"
    class="guide-image-wrapper"
    :class="{ 'is-clickable': Boolean(resolvedSrc) }"
    @click="showTargetFile"
  >
    <picture v-if="resolvedSrc">
      <source
        v-for="source in displaySources"
        :key="`${source.type}-${source.srcset}`"
        :srcset="source.srcset"
        :type="source.type"
      >
      <img
        class="guide-image"
        :src="resolvedSrc"
        :alt="alt"
        :loading="props.priority ? 'eager' : 'lazy'"
        :fetchpriority="props.priority ? 'high' : 'auto'"
        decoding="async"
      >
    </picture>
    <div v-else class="guide-image-placeholder"></div>
  </div>
</template>

<script setup>
import {computed, defineProps, onBeforeUnmount, onMounted, ref, watch} from "vue"
import {api as viewerApi} from "v-viewer"

const props = defineProps({
  url: {
    type: [String, Function, Object],
    required: true,
  },
  alt: {
    type: String,
    default: "guide image",
  },
  priority: {
    type: Boolean,
    default: false,
  },
  rootMargin: {
    type: String,
    default: "240px 0px",
  },
  preferOriginal: {
    type: Boolean,
    default: false,
  },
})

const containerRef = ref(null)
const resolvedSrc = ref("")
const resolvedSources = ref([])
const hasEnteredViewport = ref(false)
const displaySources = computed(() => props.preferOriginal ? [] : resolvedSources.value)

let observer = null
let loadTaskId = 0

const normalizeImageConfig = async (value) => {
  if (!value) {
    return {src: "", sources: []}
  }

  if (typeof value === "function") {
    return normalizeImageConfig(await value())
  }

  if (typeof value === "string") {
    return {src: value, sources: []}
  }

  return {
    src: value.src || "",
    sources: Array.isArray(value.sources) ? value.sources.filter(Boolean) : [],
  }
}

const loadImage = async () => {
  if (resolvedSrc.value) {
    return
  }

  const taskId = ++loadTaskId
  const imageConfig = await normalizeImageConfig(props.url)

  if (taskId !== loadTaskId) {
    return
  }

  resolvedSrc.value = imageConfig.src
  resolvedSources.value = imageConfig.sources
}

const ensureImageReady = async () => {
  hasEnteredViewport.value = true
  await loadImage()
}

const stopObserver = () => {
  if (observer) {
    observer.disconnect()
    observer = null
  }
}

const setupObserver = () => {
  if (!containerRef.value) {
    return
  }

  if (props.priority) {
    ensureImageReady()
    return
  }

  if (typeof IntersectionObserver === "undefined") {
    ensureImageReady()
    return
  }

  observer = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      ensureImageReady()
      stopObserver()
    }
  }, {
    rootMargin: props.rootMargin,
  })

  observer.observe(containerRef.value)
}

const showTargetFile = async () => {
  await ensureImageReady()

  if (!resolvedSrc.value) {
    return
  }

  viewerApi({
    images: [{src: resolvedSrc.value}],
  })
}

watch(() => props.url, () => {
  loadTaskId += 1
  resolvedSrc.value = ""
  resolvedSources.value = []

  if (hasEnteredViewport.value) {
    loadImage()
  }
})

onMounted(() => {
  setupObserver()
})

onBeforeUnmount(() => {
  stopObserver()
})
</script>

<style lang="less" scoped>
.guide-image-wrapper {
  margin: 10px 0;
  border-radius: 6px;
  border: 1px solid #e7e9e8;
  overflow: hidden;
  background: #f7f8fa;
}

.guide-image-wrapper.is-clickable {
  cursor: pointer;
}

.guide-image-wrapper picture {
  display: block;
}

.guide-image,
.guide-image-placeholder {
  display: block;
  width: 100%;
}

.guide-image {
  height: auto;
}

.guide-image-placeholder {
  min-height: 120px;
}
</style>
